import type { Point, SceneNode } from "../scene/model";
import { bounds, pointBounds } from "./geometry";
export interface GridSettings {
  mode: number;
  step: number;
  edges: boolean;
  vertices: boolean;
}
export function gridPoint(
  p: Point,
  size: Point,
  zoom: number,
  grid: GridSettings,
): Point {
  if (!grid.mode || (!grid.edges && !grid.vertices)) return p;
  const threshold = 10 / Math.max(0.1, zoom);
  const dx =
    size.x / 2 + Math.round((p.x - size.x / 2) / grid.step) * grid.step - p.x;
  const dy =
    size.y / 2 + Math.round((p.y - size.y / 2) / grid.step) * grid.step - p.y;
  if (grid.vertices && (Math.abs(dx) > threshold || Math.abs(dy) > threshold))
    return p;
  return {
    x: p.x + (Math.abs(dx) <= threshold ? dx : 0),
    y: p.y + (Math.abs(dy) <= threshold ? dy : 0),
  };
}
export function gridMove(
  nodes: readonly SceneNode[],
  delta: Point,
  size: Point,
  zoom: number,
  grid: GridSettings,
): Point {
  if (!grid.mode || (!grid.edges && !grid.vertices) || !nodes.length)
    return delta;
  const box = pointBounds(
    nodes.flatMap((n) => {
      const b = bounds(n);
      return [
        { x: b.x + delta.x, y: b.y + delta.y },
        { x: b.x + b.width + delta.x, y: b.y + b.height + delta.y },
      ];
    }),
  );
  const nearest = (values: number[], origin: number) => {
    let best = 0,
      distance = Infinity;
    for (const value of values) {
      const change =
        origin + Math.round((value - origin) / grid.step) * grid.step - value;
      if (
        Math.abs(change) <= 10 / Math.max(0.1, zoom) &&
        Math.abs(change) < distance
      ) {
        best = change;
        distance = Math.abs(change);
      }
    }
    return best;
  };
  return {
    x:
      delta.x +
      nearest(
        grid.vertices ? [box.x + box.width / 2] : [box.x, box.x + box.width],
        size.x / 2,
      ),
    y:
      delta.y +
      nearest(
        grid.vertices ? [box.y + box.height / 2] : [box.y, box.y + box.height],
        size.y / 2,
      ),
  };
}
